import BaseListPresenter from "../../../../base/BaseListPresenter";
import { v4 as uuidv4 } from "uuid";
import { findObjectUseCase } from "../../../../usecases/object";

export default class NegativeRecordsCompanyListPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    upsertUseCase,
    deleteObjectUseCase
  ) {
    super(
      view,
      findObjectUseCase,
      countObjectUseCase,
      upsertUseCase,
      deleteObjectUseCase
    );
    this.upsertUseCase = upsertUseCase;
    this.deleteObjectUseCase = deleteObjectUseCase;
  }

  componentDidMount() {
    this.init();
    this.getAffialiateOutSide();
    // this.getAccessRequest();
    this.getObjects();
  }

  async findObjects() {
    if (Object.keys(this.search).length === 0) {
      this.objects = [];
      this.hideProgress();
      return;
    }

    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    const currentUser = this.view.getCurrentUser();

    this.showProgress();
    this.progress = true;
    this.findObjectUseCase.abort();

    try {
      // 1) primary lookup
      let results = await this.findObjectUseCase.execute(collection, query);

      const searchQuery = {
        where: {
          company: query.where.company,
        },
      };

      // 2) if primary returned nothing, fall back to search_logs
      if (!Array.isArray(results) || results.length === 0) {
        results = await this.findObjectUseCase.execute(
          "no_records",
          searchQuery
        );
      }

      // 3) merge into your master list
      this.objects = this.objects.concat(results);
      this.view.setTotal(this.objects.length);
      this.view.setObjects(this.objects);
    } catch (error) {
      this.view.showError(error);
    } finally {
      this.hideProgress();
      this.progress = false;
    }
  }

  async onClickView(index) {
    const object = this.objects[index];

    if (object.lockedTo?.id === null) {
      object.lockedTo.id = "";
    }

    // ADD ACCESS HISTORY
    const currentUser = this.view.getCurrentUser();
    const accessObject = {
      accessor: { id: currentUser.id },
      record: { id: object?.searchNameId },
    };
    await this.upsertUseCase.execute("access_history", accessObject);
    this.view.navigateTo(object.id);
  }

  async searchSubmit(search) {
    const currentUser = this.view.getCurrentUser();
    const affiliateId = currentUser?.affiliate?.id;

    const [affiliate] = await this.findObjectUseCase.execute("affiliates", {
      where: { id: affiliateId },
    });

    const isPrepaid = affiliate?.billingType === "Prepaid";

    const escapeRegex = (str) =>
      String(str ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const searchQuery = {
      company: { $regex: escapeRegex(search.company), $options: "i" },
    };

    this.showProgress();
    this.reset();
    this.search = searchQuery;

    // Check for recent (within 30days) log
    const recentLog = await this.getRecentSearchLog(affiliateId, search);

    if (!recentLog) {
      // ✅ Deduct credits ONLY IF not found in last 24 hours
      if (isPrepaid) {
        const [credits] = await this.findObjectUseCase.execute("credits", {
          where: { "client.id": affiliateId },
        });

        if (!credits || credits.balance <= 0) {
          this.view.showError("No balance. Please contact admin.", "Error");
          this.hideProgress();
          return;
        }

        credits.balance -= 1;
        await this.upsertUseCase.execute("credits", credits);
      }

      // Log the search
      await this.addSearchLog(search);
    } else {
      this.view.setSearchLog(recentLog);
    }

    // GET NEW OBJECTS
    await this.getObjects();
    this.view.setSearchStatus("searched");
    this.hideProgress();
    this.view.setState({ searchBtnDisabled: false });
  }

  async getRecentSearchLog(affiliateId, search) {
    const logs = await this.findObjectUseCase.execute("search_logs", {
      where: {
        searcher: { affiliate: { id: affiliateId } },
        searched_company: search.company,
      },
      include: ["searcher.affiliate"],
    });

    logs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const latestLog = logs[0];
    if (!latestLog) return null;

    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(now.getMonth() - 1); // accurately subtracts 1 calendar month

    const logTime = new Date(latestLog.createdAt);

    return logTime >= oneMonthAgo ? latestLog : null;
  }

  async addSearchLog(searched) {
    const currentUser = this.view.getCurrentUser();

    // Fetch the last saved record to determine the next referenceNo
    const lastLog = await this.findObjectUseCase.execute("search_logs", {
      orderBy: { created_at: "desc" },
      limit: 1,
    });

    // Extract year and the last reference number, if available
    const currentYear = new Date().getFullYear();
    let nextReferenceNo = `${currentYear}-0000000001`; // Default referenceNo

    if (lastLog && lastLog.length > 0) {
      const lastReferenceNo = lastLog[0].referenceNo;
      const lastYear = lastReferenceNo.split("-")[0];

      if (lastYear === currentYear.toString()) {
        // Increment the last reference number by 1
        const lastNumber = parseInt(lastReferenceNo.split("-")[1], 10);
        const newNumber = lastNumber + 1;
        nextReferenceNo = `${currentYear}-${newNumber
          .toString()
          .padStart(10, "0")}`;
      }
    }

    const newLog = {
      searcher: currentUser,
      searched_first_name: searched.firstName,
      searched_middle_name: searched.middleName,
      searched_last_name: searched.lastName,
      searched_alias: searched.alias,
      searched_company: searched.company,
      referenceNo: nextReferenceNo, // Add the new referenceNo
    };

    try {
      const log = await this.upsertUseCase.execute("search_logs", newLog);
      const [affiliate] = await this.findObjectUseCase.execute("affiliates", {
        where: { id: log.searcher.affiliate?.id },
      });
      this.view.setSearchLog({ ...log, affiliateName: affiliate?.name });
    } catch (error) {
      this.view.showError(error);
    }
  }

  async deductCredit(credit) {
    try {
      const newCredit = {
        ...credit,
        balance: credit.balance - 1,
      };

      await this.upsertUseCase.execute("credits", newCredit);
    } catch (error) {
      this.view.showError(error);
    }
  }

  async getAffialiateOutSide() {
    const collection = "affiliates";
    const query = {
      where: {
        group: "OUTSIDE",
        isShowOutSide: true,
      },
    };

    try {
      const response = await findObjectUseCase().execute(collection, query);
      this.view.setOutSideClient(response);
    } catch (error) {}
  }

  async onClickSaveNoRecord(searched) {
    const currentUser = this.view.getCurrentUser();
    const outside = this.view.state.outSideClient;
    const outsideName = outside.map((out) => out.name);
    const financeCompanies = outsideName;

    const notUnlock = await findObjectUseCase().execute("users", {
      where: { id: currentUser.id },
      include: ["all"],
    });

    const userName = notUnlock?.[0]?.affiliate?.name;

    const isFinance = financeCompanies.includes(userName);

    const saveNoRecord = {
      searchNameId: uuidv4(),
      company: searched?.searched_company,
      lockedTo: { id: currentUser?.id },
      noRecord: true,
      lockedDate: new Date().toISOString(),
    };

    try {
      if (!isFinance) {
        await this.upsertUseCase.execute("no_records", saveNoRecord);
      }
    } catch (error) {}
  }
}
